import { useAuth0 } from '#~/auth/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { KONAMI_SEQUENCE, toGlyph, useKonamiCode } from '#~/konami/use-konami-code.js';
import { cn } from '#~/utils/cn.js';
import { Icon } from '@studio/design-system/icon';
import { Loader } from '@studio/design-system/loader';
import { motion } from '@studio/design-system/motion';
import { config } from '#~/config/index.js';
import { type ReactElement, useState } from 'react';
import { BuildInfo } from './build-info.js';

const getAllowedEmailsFromEnv = (): string[] => {
  const envEmails = config.buildInfoAllowedEmails;
  if (envEmails && typeof envEmails === 'string') {
    return envEmails
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean);
  }
  return [];
};

const ACCESS_CONFIG = {
  allowedEmails: getAllowedEmailsFromEnv(),
  allowedUserIds: [] as string[],
  allowAllAdministrators: true
};

const PASSCODE_STYLES = `
  @keyframes cursor-blink {
    0%, 100% { border-color: var(--color-muted); }
    50% { border-color: transparent; }
  }
  .vault-cursor {
    animation: cursor-blink 1.2s step-end infinite;
  }
  @keyframes scanlines {
    0% { background-position: 0 0; }
    100% { background-position: 0 4px; }
  }
`;

const PasscodeCell = ({
  code,
  filled,
  isCurrent,
  index
}: {
  readonly code: string;
  readonly filled: boolean;
  readonly isCurrent: boolean;
  readonly index: number;
}) => (
  <motion.div
    className={cn(
      'flex h-64 w-64 items-center justify-center rounded-16 border-2 font-mono text-4xl',
      filled
        ? 'border-success bg-success/10 text-success'
        : isCurrent
          ? 'vault-cursor border-muted bg-base-200 text-muted'
          : 'border-base-100 bg-base-200 text-muted/25'
    )}
    animate={
      filled
        ? {
            scale: [0.9, 1.1, 1],
            boxShadow: [
              '0 0 0px rgba(var(--color-success-raw, 74 222 128), 0)',
              '0 0 16px rgba(var(--color-success-raw, 74 222 128), 0.3)',
              '0 0 6px rgba(var(--color-success-raw, 74 222 128), 0.1)'
            ]
          }
        : { scale: 1, boxShadow: '0 0 0px transparent' }
    }
    transition={{ duration: 0.25, ease: 'easeOut', delay: filled ? index * 0.02 : 0 }}
  >
    {filled ? toGlyph(code) : '\u2022'}
  </motion.div>
);

const PasscodeView = ({ position }: { readonly position: number }) => {
  const progress = position / KONAMI_SEQUENCE.length;

  return (
    <>
      <style>{PASSCODE_STYLES}</style>
      <div className='flex min-h-screen select-none flex-col items-center justify-center bg-base-300'>
        <motion.div
          className='flex flex-col items-center gap-32'
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Lock */}
          <motion.div
            className='text-muted'
            animate={{ opacity: position > 0 ? 1 : 0.5 }}
            transition={{ duration: 0.3 }}
          >
            <Icon icon='lock' className='text-5xl' />
          </motion.div>

          {/* Header */}
          <div className='flex flex-col items-center gap-8'>
            <h1 className='text-foreground/70 text-3xl font-medium uppercase tracking-[0.2em]'>
              Restricted
            </h1>
            <p className='text-xl text-muted'>Sequence required</p>
          </div>

          {/* Cells */}
          <div className='flex gap-10'>
            {KONAMI_SEQUENCE.map((code, i) => (
              <PasscodeCell
                key={i}
                code={code}
                filled={i < position}
                isCurrent={i === position}
                index={i}
              />
            ))}
          </div>

          {/* Progress track */}
          <div className='h-1 w-[32rem] overflow-hidden rounded-full bg-base-100'>
            <motion.div
              className='h-full rounded-full bg-success/50'
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>

          {/* Status */}
          <motion.p
            className='text-lg tracking-wider text-muted/50'
            animate={{ opacity: position > 0 ? 1 : 0.6 }}
            transition={{ duration: 0.2 }}
          >
            {position > 0 ? `${position}\u2009/\u2009${KONAMI_SEQUENCE.length}` : 'Listening\u2026'}
          </motion.p>
        </motion.div>
      </div>
    </>
  );
};

export const ProtectBuildInfo = (): ReactElement => {
  const { isLoading } = useAuth0();
  const user = useDepot((µ) => µ.user);
  const [konamiUnlocked, setKonamiUnlocked] = useState(false);

  const { position } = useKonamiCode(() => setKonamiUnlocked(true));

  // BootOverlay dismissal for this route is owned by <BootReadyLayout> (router.tsx),
  // which publishes boot.isRootReady on mount; this route no longer does (ENG-4186).

  if (isLoading || !user) {
    return <Loader />;
  }

  const isAuthorized =
    ACCESS_CONFIG.allowedEmails.includes(user.email) ||
    ACCESS_CONFIG.allowedUserIds.includes(user.id as string);

  if (konamiUnlocked) {
    return <BuildInfo konamiUnlocked />;
  }

  if (!isAuthorized) {
    return <PasscodeView position={position} />;
  }

  return <BuildInfo />;
};
